import { offset } from '@floating-ui/dom';
import { tourBackButton, tourCustomButton, tourExitButton, tourNextButton } from '../data/tour-buttons';
import { type TourStepDefinition } from '../data/tour-config';
import { getTargetElementIdOrFallback } from '../data/tour-functions';
import { TourService } from '../services/tour/tour.service';

export function getAppointmentsTour(tourService: TourService): TourStepDefinition[] {
  return [
    {
      id: 'appointmentsTourIntro',
      buttons: [tourExitButton(tourService, 'Skip Tour'), tourNextButton('Start Tour')],
      title: 'Welcome to Your Appointments!',
      text: "Let's take a quick tour to help you manage your healthcare visits.",
    },
    {
      id: 'appointmentsTourUpcomingAppointments',
      attachTo: {
        element: 'app-upcoming-appointments-widget',
        on: 'auto',
      },
      scrollTo: false,
      buttons: [tourExitButton(tourService), tourBackButton(), tourNextButton('Next (1 of 3)')],
      title: 'Your Upcoming Appointments section shows your scheduled visits',
      text: () =>
        `<div class="flex flex-col gap-6">
      <ul>
      <li><strong>Date and time</strong> of your visit</li>
      <li><strong>Provider name</strong></li>
      <li><strong>Location address</strong> with full street address, city, and zip code</li>
      <li><strong>Phone number</strong> to call if you need to cancel or reschedule</li>
      </ul>
      <span>Tap any appointment to see more details, get directions, or add it to your calendar.</span> 
      </div>`,
    },
    {
      id: 'appointmentsTourPastAppointments',
      attachTo: {
        //check if past appointment element exists; if not, attach to the widget itself
        element: () => getTargetElementIdOrFallback('pastAppointment0', 'app-past-appointments-widget'),
        on: 'auto',
      },
      scrollTo: true,
      buttons: [tourExitButton(tourService), tourBackButton(), tourNextButton('Next (2 of 3)')],
      title: 'Your Past Appointments section keeps a record of previous visits',
      text: () =>
        `<div class="flex flex-col gap-6">
      <span>For each past appointment, you can:</span> 
      <ul>
      <li>See the <strong>date</strong> and <strong>appointment type</strong> (like "Respiratorytherapy")</li>
      <li>Find the <strong>phone number</strong> for that location</li>
      <li>Tap <strong>"View Visit Summary"</strong> to see a summary of what was discussed</li>
      <li>Tap <strong>"View Visit Note"</strong> to read your provider's clinical notes</li>
      </ul>
      <span>Scroll down to see your complete appointment history.</span> 
      </div>`,
    },
    {
      id: 'appointmentsTourScheduleAppointment',
      attachTo: {
        element: '#scheduleAppointmentBtn',
        on: 'auto',
      },
      scrollTo: true,
      floatingUIOptions: {
        middleware: [offset(12)], //offset the step 12 pixels away from the target element
      },
      buttons: [tourBackButton('Back', ['two-row-btn']), tourNextButton('Done (3 of 3)')],
      title: 'Ready to schedule your next visit?',
      text: () =>
        `<div class="flex flex-col gap-10">
        <span>Tap <strong>"Schedule New Appointment"</strong> to:</span>
        <ul>
        <li>Request a new appointment with your provider</li>
        <li>Choose your preferred date and time</li>
        <li>Select the reason for your visit</li>
        <li>Receive confirmation once your appointment is scheduled</li>
        </ul>
        <span><strong>Note:</strong> Appointment requests may take up to 72 hours to process. For urgent medical needs, please call your provider's office directly using the phone numbers listed above.</span> 
      </div>`,
    },
    {
      id: 'appointmentsTourOutro',
      buttons: [tourCustomButton('Got it', 'primary', () => tourService.complete())],
      title: "You're All Set!",
      text: () =>
        `<div class="flex flex-col gap-6">
        <span><strong>You can access this tour again anytime from the help menu.</strong></span>
      </div>`,
    },
  ];
}
